import * as Sentry from '@sentry/browser';
import { returningUserReward } from '../Config/Items';
import { getGameSize } from '../Config/Levels';
import { Tracker } from '../DeltaTracking/Tracker';
import { app } from '../index';
import Game from '../Managers/Game';
import GameContainer from '../Managers/Game';
import { IAdStages, Tracking } from '../Managers/Tracking/Tracking';
import { Platform, SDK } from '../SDK/SDK';
import { Actions } from '../State/Actions/Actions';
import GameStore from '../State/Store/store';
import Localisation from '../Utils/Localisation';
import GameView from '../View/GameView';
import GameLoader from './Assets';
import Debug from './Debug';
import DateUtils from '../Utils/DateUtils';
import AdTracking from '../Managers/Tracking/AdTracking';
import { AdTypes } from '../DeltaTracking/AdParamBuilder';

export enum GameOrientation {
    LANDSCAPE = 'landscape',
    PORTRAIT = 'portrait',
}

declare global {
    const ASSET_BUNDLES: string;
}

export let loadStart: number;
export let gameStarted: boolean = false;

export default class Core {
    public static sdk: SDK;
    public static store: GameStore;
    public static component: GameView;
    public static container: Game;
    public static stats: Stats;
    //  public static tracking: Tracking;
    public static delta: Tracker;
    public static DEBUG: boolean;
    public static gameCache: any = {
        atlases: {},
        bitmapfonts: {},
        images: {},
        imagesLandscape: {},
        levels: {},
        sfx: {},
        jsons: {} as any,
        avatars: {} as any,
    };
    public static local: Localisation;
    public static orientation: GameOrientation = GameOrientation.PORTRAIT;

    public static async init() {
        Core.sdk = await initPlatform();
        Core.orientation =
            window.innerWidth > window.innerHeight
                ? GameOrientation.LANDSCAPE
                : GameOrientation.PORTRAIT;
        // Core.sdk.getPlatform() === 'WEB' ? GameOrientation.LANDSCAPE : GameOrientation.PORTRAIT;
        app.ticker.add(gameLoop);
        initLoad();
    }

    public static async loadComplete() {
        const restoreData = await Core.sdk.data.loadSdkData();
        setUpGame(restoreData);
        const isNewDevice = await setUpTracking();
        Tracking.startTracking(isNewDevice, getNewPlayer(restoreData));
        await processEntryData();
        await rewardReturningPlayer(restoreData);
        setUpDebug();
        startGame();
    }

    // helpers for ad tracking to get around issues with enums and static functions
    public static adTrackingHelper(
        adType: AdTypes,
        stage: IAdStages,
        placement: string,
        loadtime: number,
        uuAdId: string,
        adAction: string
    ) {
        const t: AdTypes = adType === 'rewarded' ? AdTypes.REWARDED : AdTypes.INTERSTITIAL;
        AdTracking.track(t, stage, placement, loadtime, uuAdId, adAction);
    }

    public static tooManyAds() {
        AdTracking.tooManyAds();
    }
}

function initLoad() {
    GameLoader.initGameLoader();
    loadStart = Date.now();

    try {
        GameLoader.loadAssetList({
            assetList: GameLoader.bundleMap.get('boot'),
            onProgress: Core.sdk.loadProgress.bind(Core.sdk),
            cb: Core.loadComplete,
            oncomplete: Core.sdk.loadComplete.bind(Core.sdk),
        })
            .then()
            .catch(err => {
                Sentry.captureException(err);
                Sentry.captureMessage('error with boot loading:' + err);
            });
    } catch (e) {
        Sentry.captureException(e);
        Sentry.captureMessage('error with boot loading wrapped:' + e);
    }
}

function setUpGame(loaded: any) {
    Core.store = new GameStore(loaded);
    Core.component = new GameView();
    app.stage.addChild(Core.component);
    Core.local = new Localisation();
    Core.container = new GameContainer(Core.component);
}

async function rewardReturningPlayer(data: any) {
    if (Core.sdk.backend && (await Core.sdk.backend.checkConnection())) {
        const serverTime = await Core.sdk.backend.fetchServerTimestamp();
        const { lastLogin } = data;
        if (lastLogin) {
            const inactivity = DateUtils.elapsedDays(lastLogin, serverTime.valueOf());
            const reward = returningUserReward.filter(x => inactivity >= x.days).pop();
            if (reward) {
                const { item } = reward;
                Core.store.dispatch(Actions.receiveGift([item.id, item.val as number]));
            }
        }
        await Core.sdk.data.saveData('lastLogin', serverTime);
    }
}

function startGame() {
    gameStarted = true;
}

const coroutines: Array<IterableIterator<unknown>> = [];

export function startCoroutine<T>(routine: IterableIterator<T>) {
    coroutines.push(routine);
}

const MAX_TIME_PER_FRAME = 16;

function coroutineTicker(initialTime: number) {
    const deltaTime = () => Date.now() - initialTime;
    const removeList = [];
    for (const routine of coroutines) {
        if (deltaTime() >= MAX_TIME_PER_FRAME) {
            break;
        }
        const next = routine.next();
        if (next.done) {
            removeList.push(routine);
        }
    }
    for (const routine of removeList) {
        const index = coroutines.indexOf(routine);
        coroutines.splice(index, 1);
    }
}

function gameLoop() {
    const initialTime = Date.now();
    if (gameStarted && Core.container && Core.store) {
        if (Core.stats) {
            Core.stats.begin();
        }
        Core.container.update().catch(err => {
            console.log(['error with container update', err]);
        });

        if (Core.stats) {
            Core.stats.end();
        }
    }
    coroutineTicker(initialTime);
}

async function initPlatform(): Promise<any> {
    const sdk = new SDK();
    await sdk.initSdk(sgPlatform as Platform).catch(err => {
        this.coreLogger.debug(' error with initSDK: ' + err);
        throw err;
    });

    return sdk;
}

async function processEntryData(): Promise<any> {
    const { entryPointData } = Core.sdk.user;

    if (entryPointData) {
        await Core.sdk.messages.setEntryPointData(entryPointData);
    }
    Core.sdk.messages.processPayloads();
    await Core.sdk.messages.processConfirmedInvites();
    await Core.sdk.messages.processReceivedInvites();
}

async function setUpTracking(): Promise<boolean> {
    return await Tracking.setUpTracking();
}

function getNewPlayer(restoreData: any): boolean {
    return !restoreData.played || Object.keys(restoreData.played).length === 0;
}

function gameResize() {
    if (Core.delta) {
        Core.delta.setSessionParams({
            screenWidth: getGameSize().gameWidth || 0,
            screenHeight: getGameSize().gameHeight || 0,
        });
    }
    if (Core.component) {
        const size = getGameSize();
        Core.component.resize(size);
    }
}

function setUpDebug() {
    Core.DEBUG = DEBUG;
    if (STATS) {
        Core.stats = createStats();
    }
    Debug.initDebugCommands();
}

function createStats() {
    const stats = new window.Stats();
    stats.showPanel(2);
    const { dom } = stats;
    dom.style.cssText = 'position:fixed;top:0;left:0;cursor:pointer;opacity:0.9;z-index:10000';
    document.body.appendChild(dom);

    return stats;
}

export { gameResize };
